import { PHONEMES } from "../../content/phonemes";
import type { Card } from "../../content/types";
import { isMastered } from "../../engine/mastery";
import { startSession } from "../../state/session";
import type { Profile } from "../../state/profile";
import { Button, Sheet } from "../../components";
import { ArticulatorDiagram } from "./ArticulatorDiagram";
import { CATEGORY_LABEL, daysUntilDue, STATUS_LABEL, cardStatus } from "./utils";

type CardSheetProps = { card: Card | null; profile: Profile; today: string; onClose: () => void };

const PHONEME_BY_ID = new Map(PHONEMES.map((p) => [p.id, p]));

export function CardSheet({ card, profile, today, onClose }: CardSheetProps) {
  if (!card) return <Sheet open={false} onClose={onClose} />;

  const srsCard = profile.cards[card.id];
  const status = cardStatus(card.id, profile, today);
  const until = daysUntilDue(srsCard, today);
  const wpm = profile.bests.wpmByCard[card.id];

  return (
    <Sheet open={!!card} onClose={onClose} labelledBy="card-sheet-title">
      <p class="label">{card.kind === "twister" ? `Level ${card.level ?? 1}` : card.category ? CATEGORY_LABEL[card.category] : "Phrase"}</p>
      <h2 id="card-sheet-title" class="display card-sheet-text">
        {card.text}
      </h2>
      {(card.meaning || card.gloss) && <p class="card-sheet-gloss">{card.meaning ?? card.gloss}</p>}
      {card.scenario && (
        <p class="card-sheet-scenario">
          <span class="label">Scenario</span> {card.scenario}
        </p>
      )}

      <div class="card-sheet-stats mono">
        <span>{STATUS_LABEL[status]}</span>
        <span>{wpm ? `${wpm} wpm best` : "no wpm yet"}</span>
        <span>{until === null ? "not started" : until <= 0 ? "due now" : `due in ${until}d`}</span>
        {isMastered(srsCard) && <span class="card-sheet-mastered">Mastered</span>}
      </div>

      {card.kind === "twister" && card.phonemes.length > 0 && (
        <div class="card-sheet-phonemes">
          <p class="label">Sounds trained</p>
          <div class="card-sheet-phoneme-list">
            {card.phonemes.map((id) => {
              const ph = PHONEME_BY_ID.get(id);
              if (!ph) return null;
              return (
                <div class="card-sheet-phoneme" key={id}>
                  <ArticulatorDiagram id={ph.diagramId} size={56} />
                  <div>
                    <p class="mono card-sheet-phoneme-label">{ph.label}</p>
                    <p class="card-sheet-phoneme-cue">{ph.cue}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Button
        size="lg"
        onClick={() => {
          startSession("free");
          onClose();
        }}
      >
        Practice now
      </Button>
    </Sheet>
  );
}
